import React, { useMemo, useState } from "react";
import { useAddress } from "@api/entity.api";
import { Address } from "@common/types/embeddable/address";
import {
    EntityProfile,
    EntityProfileIndustry,
    EntityProfileType,
} from "@common/types/entity";
import { SectionHeader } from "@design-system/Section/Section";
import { useEntity } from "../../entity/useEntity";
import { industryLabels } from "../../entity/constants";
import { SettingRow } from "../General/SettingRow";
import { EntityLogoSection } from "./EntityLogoSection";
import styles from "./EntityGeneralSettings.module.scss";
import { UpdateNameModal } from "./Modals/UpdateNameModal";
import { UpdateWebsiteModal } from "./Modals/UpdateWebsiteModal";
import { UpdateDescriptionModal } from "./Modals/UpdateDescriptionModal";
import { UpdateAddressModal } from "./Modals/UpdateAddressModal";
import { UpdateIndustriesModal } from "./Modals/UpdateIndustriesModal";
import { UpdateBookkeepingStartDateModal } from "./Modals/UpdateBookkeepingStartDateModal";
import { EntityBookkeepingStartDateRow } from "./EntityBookkeepingStartDateRow";
import { ENTITY_TYPE_LABELS } from "./entityTypeLabels";

const EntityNameRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Entity name"
            value={entity.name}
            buttonText="Edit name"
            onButtonClick={onEdit}
        />
    );
};

const EntityDescriptionRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Description"
            value={entity.profile?.description}
            buttonText="Edit description"
            onButtonClick={onEdit}
        />
    );
};

const EntityWebsiteRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Website"
            value={entity.profile?.website}
            buttonText="Edit website"
            onButtonClick={onEdit}
        />
    );
};

const EntityEntityTypeRow: React.FC = () => {
    const entity = useEntity();
    return (
        <SettingRow
            label="Entity type"
            value={
                ENTITY_TYPE_LABELS[
                    entity.legalType ?? EntityProfileType.NO_LEGAL_ENTITY
                ]
            }
        />
    );
};

const getDisplayIndustries = ({
    industries,
    industry,
    industryOther,
}: EntityProfile): string => {
    let list: EntityProfileIndustry[] = [];
    if ((industries?.length ?? 0) > 0) {
        list = industries ?? [];
    } else if (industry != null) {
        list = [industry];
    }

    return list
        .map((v) => {
            if (v === EntityProfileIndustry.OTHER) {
                return industryOther && industryOther.length
                    ? industryOther
                    : industryLabels.other;
            }
            return industryLabels[v];
        })
        .join(", ");
};

const EntityIndustriesRow: React.FC<{ onEdit: () => void }> = ({ onEdit }) => {
    const entity = useEntity();
    const displayValue = useMemo(
        () => (entity.profile ? getDisplayIndustries(entity.profile) : ""),
        [entity.profile],
    );
    return (
        <SettingRow
            label="Industry"
            value={displayValue}
            buttonText="Edit industry"
            onButtonClick={onEdit}
        />
    );
};

const createDisplayAddress = (address: Partial<Address>) => {
    let final = "";
    if (address.street) {
        final += address.street;
    }
    if (address.street2) {
        final += ", " + address.street2;
    }
    if (address.city) {
        final += ", " + address.city;
    }
    if (address.state) {
        final += ", " + address.state;
    }
    if (address.postalCode) {
        final += ", " + address.postalCode;
    }
    return final;
};

const EntityAddressRow: React.FC<{
    onEdit: () => void;
    address?: Partial<Address>;
}> = ({ onEdit, address }) => (
    <SettingRow
        label="Business address"
        value={createDisplayAddress(address ?? {})}
        buttonText="Edit address"
        onButtonClick={onEdit}
    />
);

export const EntityGeneralSettings: React.FC = () => {
    const [showNameUpdate, setShowNameUpdate] = useState(false);
    const [showDescriptionUpdate, setShowDescriptionUpdate] = useState(false);
    const [showWebsiteUpdate, setShowWebsiteUpdate] = useState(false);
    const [showAddressUpdate, setShowAddressUpdate] = useState(false);
    const [showIndustriesUpdate, setShowIndustriesUpdate] = useState(false);
    const [showBookkeepingStartDateUpdate, setShowBookkeepingStartDateUpdate] =
        useState(false);

    const entity = useEntity();
    const address = useAddress(entity.id);

    return (
        <section className="mb-5">
            <SectionHeader>
                <h4>
                    {entity.isBusiness ? "Business" : "Personal"} information
                </h4>
            </SectionHeader>

            <div className={styles.settingsContainer}>
                {entity.isBusiness && (
                    <>
                        <EntityNameRow onEdit={() => setShowNameUpdate(true)} />
                        <EntityEntityTypeRow />
                        <EntityBookkeepingStartDateRow
                            onEdit={() =>
                                setShowBookkeepingStartDateUpdate(true)
                            }
                        />
                        <EntityDescriptionRow
                            onEdit={() => setShowDescriptionUpdate(true)}
                        />
                        <EntityWebsiteRow
                            onEdit={() => setShowWebsiteUpdate(true)}
                        />
                    </>
                )}
                <EntityAddressRow
                    onEdit={() => setShowAddressUpdate(true)}
                    address={address.data ?? undefined}
                />
                <EntityIndustriesRow
                    onEdit={() => setShowIndustriesUpdate(true)}
                />
                <EntityLogoSection />
            </div>

            <UpdateNameModal
                show={showNameUpdate}
                onClose={() => setShowNameUpdate(false)}
            />
            <UpdateWebsiteModal
                show={showWebsiteUpdate}
                onClose={() => setShowWebsiteUpdate(false)}
            />
            <UpdateDescriptionModal
                show={showDescriptionUpdate}
                onClose={() => setShowDescriptionUpdate(false)}
            />
            <UpdateAddressModal
                show={showAddressUpdate}
                address={address.data ?? undefined}
                onClose={() => setShowAddressUpdate(false)}
            />
            <UpdateIndustriesModal
                show={showIndustriesUpdate}
                onClose={() => setShowIndustriesUpdate(false)}
            />
            {entity.isBusiness && (
                <UpdateBookkeepingStartDateModal
                    show={showBookkeepingStartDateUpdate}
                    onClose={() => setShowBookkeepingStartDateUpdate(false)}
                />
            )}
        </section>
    );
};
